import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { API } from '@/api'
import { Divider, Modal, notification, NumberInput, Trans } from '@/components'
import { InsufficientQuotaView } from '@/components/InsufficientQuotaView'
import { errorMsg } from '@/consts/errorMsg'
import { MachineType, PaymentType } from '@/consts/machine'
import {
  BILLING_METHODS,
  type BillingMethod,
  DURATION,
  type Duration,
  // InstancePlanItem,
  ItemPaymentType,
  ItemPriceType,
} from '@/consts/order'
import { hasPrivateRegionDomain } from '@/consts/privateRegion'
import { getBonusStat } from '@/models/credit'
import { Image } from '@/models/image'
import { Machine } from '@/models/machine'
import { Snapshot } from '@/models/snapshot'
import { ExchargeModal } from '@/pages/billing/components/RechargeModal'
import { useUserStore } from '@/stores/user'
import { showError } from '@/utils/error'
import { formatPrice } from '@/utils/formatter'

import { AutoRenewalSection } from './components/AutoRenewalSection'
import { BillingMethods } from './components/BillingMethods'
import { SubscriptionCreditAlert } from './components/SubscriptionCreditAlert'
import { BindNATModal } from './components/BindNATModal'
import { BindPublicIPSection } from './components/BindPublicIPSection'
import { ConfirmationModal } from './components/ConfirmationModal'
import { ExpirationBehavior } from './components/ExpirationBehavior'
import {
  type ExtraResourceChangeReason,
  ExtraSpecificationSection,
} from './components/ExtraSpecificationSection'
import { InsufficientRechargeCreditsModal } from './components/InsufficientRechargeCreditsModal'
import { InsufficientStorageModal } from './components/InsufficientStorageModal'
import { MachineSelector } from './components/machine-selector'
import { MountDriveModal } from './components/MountDriveModal'
// import { InstanceImageSwitch } from './components/InstanceImageSwitch'
import { MountDriveSection, PERSONAL_SECTION_CONFIG } from './components/MountDriveSection'
import { PurchaseForSection } from './components/PurchaseForSection'
import { SelectMachineTooltip } from './components/SelectMachineTooltip'
import { OrderSummary } from './components/summary'
import { useMachinePrice } from './hooks/useMachinePrice'
import { useMountConfig } from './hooks/useMountConfig'
import { useVolumeData } from './hooks/useVolumeData'
import { buildCreateInstancePayload, getPlanPriceByType, supportsSubscription } from './utils'
import type { CreateInstanceBoundNATInfo } from './types'
import {
  EMPTY_EXTRA_RESOURCE_CONFIG,
  ExtraResourceConfig,
  getMinDiskGB,
  getMinimumInstanceCountForBootSource,
  getMinResource,
  getRequiredExtraResourceConfig,
  getResourceRequirementStatus,
  reconcileAutoExtraResourceConfig,
} from './utils/extraStorage'

// Specific images require a minimum number of instances to function correctly
// Maps image resID to the minimum required instance count
const IMAGE_MINIMUM_INSTANCE_COUNT: Record<string, number> = {
  'img-5yp6v8l8': 4,
  'img-y4lvnxqw': 2,
}

export function CreateInstancePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const userStore = useUserStore()

  const [type, setType] = useState(MachineType.GPU)
  const period = PaymentType.Hourly
  const [machine, setMachine] = useState<Machine>()
  const [count, setCount] = useState(1)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [isStorageWarningOpen, setIsStorageWarningOpen] = useState(false)
  const [isCreatingIns, setIsCreatingIns] = useState(false)
  const [checkedImage, setCheckedImage] = useState<Image>()
  const [checkedSnap, setCheckedSnap] = useState<Snapshot>()
  const [extraResourceConfig, setExtraResourceConfig] = useState<ExtraResourceConfig>(
    EMPTY_EXTRA_RESOURCE_CONFIG,
  )
  const autoExtraResourceConfigRef = useRef<ExtraResourceConfig>(EMPTY_EXTRA_RESOURCE_CONFIG)
  const isExtraResourceUserManagedRef = useRef(false)
  const handleExtraResourceChange = useCallback(
    (value: ExtraResourceConfig, reason: ExtraResourceChangeReason) => {
      const isUserManaged = reason === 'user'

      isExtraResourceUserManagedRef.current = isUserManaged
      autoExtraResourceConfigRef.current = isUserManaged ? EMPTY_EXTRA_RESOURCE_CONFIG : value
      setExtraResourceConfig(value)
    },
    [],
  )
  const [billingMethod, setBillingMethod] = useState<BillingMethod>(BILLING_METHODS.PAY_AS_YOU_GO)
  const [duration, setDuration] = useState<Duration>(DURATION.DAILY)
  const [subscriptionTotalDays, setSubscriptionTotalDays] = useState<number | undefined>(1)
  const [purchaseFor, setPurchaseFor] = useState<number | 'custom'>(1)
  const [autoRenewal, setAutoRenewal] = useState(false)
  const [expirationBehavior, setExpirationBehavior] = useState(false)
  const [insufficientRechargeOpen, setInsufficientRechargeOpen] = useState(false)
  const [exchargeModalOpen, setExchargeModalOpen] = useState(false)
  const isSubscription = billingMethod === BILLING_METHODS.SUBSCRIPTION
  // const [instancePlans, setInstancePlans] = useState<InstancePlanItem[]>([])

  // Mount Drive
  const [mountModalOpen, setMountModalOpen] = useState(false)
  const { volumes: cloudVolumes, loading: volumesLoading } = useVolumeData(machine?.domains)
  const { mountVolume, mountPath, readWriteMode, updateMountConfig, resetMountConfig } =
    useMountConfig()

  const [boundNAT, setBoundNAT] = useState<CreateInstanceBoundNATInfo | undefined>(undefined)
  const [bindNATModalOpen, setBindNATModalOpen] = useState(false)

  // subscription orders can only spend recharge credits; mirrors the summary's price calc (cent)
  const subscriptionPriceCent = useMemo(() => {
    if (billingMethod !== BILLING_METHODS.SUBSCRIPTION || !machine) return undefined

    const paymentType =
      duration === DURATION.MONTHLY
        ? ItemPaymentType.Available30Days
        : ItemPaymentType.Available1Days
    const item = machine.items?.find((i) => i.paymentType === paymentType)
    if (!item) return undefined

    const unitPriceCent = getPlanPriceByType(item, ItemPriceType.Original) ?? 0
    const qty =
      duration === DURATION.MONTHLY
        ? Math.ceil((subscriptionTotalDays ?? 0) / 30)
        : (subscriptionTotalDays ?? 0)

    return unitPriceCent * qty * count
  }, [billingMethod, machine, duration, subscriptionTotalDays, count])

  const rechargeCreditCent = getBonusStat(userStore.account)?.rechargeBonus.moneyCent

  const {
    data: priceData,
    loading: priceLoading,
    priceAvailable,
    extraStorageGB,
    availableExtraStorageGB,
    extraCpuPriceMillicent,
    extraRamPriceMillicent,
    extraStoragePriceMillicent,
    extraResourceDesc,
  } = useMachinePrice({
    machine,
    count,
    checkedImage,
    checkedSnap,
    extraResourceConfig,
  })

  const selectedBootSource = checkedSnap ?? checkedImage
  const notifyInstanceCountAdjustment = useCallback(
    (nextCount: number) => {
      const sourceType = checkedSnap ? 'snapshot' : 'image'
      const sourceTypeLabel = t(`create.bootSourceType.${sourceType}`)

      notification.info(
        t('create.instanceCountAdjustedForBootSource', {
          count: nextCount,
          sourceType: sourceTypeLabel,
        }),
      )
    },
    [checkedSnap, t],
  )
  const clearBootSourceIfIncompatible = useCallback(
    (
      nextCount: number,
      supportsExtraResources: boolean,
      reason: 'subscription' | 'configuration',
    ) => {
      if (!selectedBootSource) return false

      const minimumInstanceCount = getMinimumInstanceCountForBootSource({
        machine,
        count: nextCount,
        source: selectedBootSource,
        supportsExtraResources,
      })
      if (minimumInstanceCount !== undefined) {
        if (minimumInstanceCount > nextCount) {
          setCount(minimumInstanceCount)
          notifyInstanceCountAdjustment(minimumInstanceCount)
        }

        return false
      }

      const sourceType = checkedSnap ? 'snapshot' : 'image'
      const sourceTypeLabel = t(`create.bootSourceType.${sourceType}`)

      setCheckedImage(undefined)
      setCheckedSnap(undefined)
      notification.warning(
        t(
          reason === 'subscription'
            ? 'create.bootSourceClearedForSubscription'
            : 'create.bootSourceClearedForConfiguration',
          { sourceType: sourceTypeLabel },
        ),
      )

      return true
    },
    [checkedSnap, machine, notifyInstanceCountAdjustment, selectedBootSource, t],
  )
  const minimumResourceInstanceCount = useMemo(
    () =>
      selectedBootSource
        ? getMinimumInstanceCountForBootSource({
            machine,
            count: machine?.unitStep ?? 1,
            source: selectedBootSource,
            supportsExtraResources: !isSubscription,
          })
        : undefined,
    [isSubscription, machine, selectedBootSource],
  )
  const minimumBootSourceInstanceCount = Math.max(
    machine?.unitStep ?? 1,
    minimumResourceInstanceCount ?? 1,
    checkedImage ? (IMAGE_MINIMUM_INSTANCE_COUNT[checkedImage.resID] ?? 1) : 1,
  )
  const selectedMinDiskGB = getMinDiskGB(selectedBootSource)
  const baseStorageGB = (machine?.unitSpec?.diskGB ?? 0) * count
  const minimumRequiredExtraStorageGB = Math.max(selectedMinDiskGB - baseStorageGB, 0)
  const requiredExtraResourceConfig = useMemo(
    () => getRequiredExtraResourceConfig(machine, count, selectedBootSource),
    [machine, count, selectedBootSource],
  )
  const requiredExtraStorageGB = requiredExtraResourceConfig.diskGB ?? 0
  const resourceRequirementStatus = getResourceRequirementStatus({
    machine,
    count,
    source: selectedBootSource,
    extraResourceConfig,
  })
  const storageInsufficient = resourceRequirementStatus.diskInsufficient

  useEffect(() => {
    setCount(machine?.unitStep ?? 1)
    setExtraResourceConfig(EMPTY_EXTRA_RESOURCE_CONFIG)
    autoExtraResourceConfigRef.current = EMPTY_EXTRA_RESOURCE_CONFIG
    isExtraResourceUserManagedRef.current = false
    resetMountConfig()
  }, [machine, resetMountConfig])

  useEffect(() => {
    if (isSubscription) {
      setExtraResourceConfig(EMPTY_EXTRA_RESOURCE_CONFIG)
      autoExtraResourceConfigRef.current = EMPTY_EXTRA_RESOURCE_CONFIG
      isExtraResourceUserManagedRef.current = false
      return
    }

    if (!machine) {
      setExtraResourceConfig(EMPTY_EXTRA_RESOURCE_CONFIG)
      autoExtraResourceConfigRef.current = EMPTY_EXTRA_RESOURCE_CONFIG
      isExtraResourceUserManagedRef.current = false
      return
    }

    setExtraResourceConfig((prev) => {
      const { config, trackedAutoConfig } = reconcileAutoExtraResourceConfig(
        machine,
        prev,
        requiredExtraResourceConfig,
        autoExtraResourceConfigRef.current,
        isExtraResourceUserManagedRef.current,
      )

      autoExtraResourceConfigRef.current = trackedAutoConfig

      return config
    })
  }, [isSubscription, machine, requiredExtraResourceConfig])

  useEffect(() => {
    if (
      !selectedBootSource ||
      minimumResourceInstanceCount === undefined ||
      count >= minimumBootSourceInstanceCount ||
      minimumBootSourceInstanceCount > (machine?.maxAvailUnits ?? 0)
    ) {
      return
    }

    setCount(minimumBootSourceInstanceCount)
    notifyInstanceCountAdjustment(minimumBootSourceInstanceCount)
  }, [
    count,
    machine,
    minimumBootSourceInstanceCount,
    minimumResourceInstanceCount,
    notifyInstanceCountAdjustment,
    selectedBootSource,
  ])
  const canCreate = useMemo(() => {
    return machine && (checkedImage || checkedSnap)
  }, [machine, checkedImage, checkedSnap])

  // const instancePlanByResID = useMemo(
  //   () => new Map(instancePlans.map((p) => [p.resID, p])),
  //   [instancePlans],
  // )

  const onConfirm = useCallback(
    async (name: string) => {
      if (!machine) return
      setIsCreatingIns(true)

      try {
        const createReq = buildCreateInstancePayload({
          machine,
          count,
          checkedImage,
          checkedSnap,
          mountVolume,
          mountPath,
          readWriteMode,
          boundNAT: billingMethod === BILLING_METHODS.PAY_AS_YOU_GO ? boundNAT : undefined,
          remark: name,
          extraResourceDesc: isSubscription ? undefined : extraResourceDesc,
        })

        if (billingMethod === BILLING_METHODS.PAY_AS_YOU_GO) {
          const res = await API.createInstance(createReq)
          if (res.code === 0) {
            setIsConfirmModalOpen(false)
            navigate('/instances')
          } else if (res.code === 194) {
            // Public IP port conflict is globally ignored by the http layer, show it here
            showError(errorMsg[194], '194')
          }
        } else {
          // if (!instancePlans.length) {
          //   console.error('No instance subscription plans loaded')
          //   setIsCreatingIns(false)
          //   return
          // }

          const paymentType =
            duration === DURATION.MONTHLY
              ? ItemPaymentType.Available30Days
              : ItemPaymentType.Available1Days

          const machineItem = machine.items?.find((i) => i.paymentType === paymentType)

          if (!machineItem) {
            console.error('No machine item for paymentType', paymentType, machine)
            setIsCreatingIns(false)
            return
          }

          const planItem = machine.items?.find((i) => i.paymentType === paymentType)
          if (!planItem) return 0

          const info = JSON.stringify(createReq)

          // const unitPriceCent =
          //   getPlanPriceByType(planItem, ItemPriceType.Original) ?? 0

          const qty =
            duration === DURATION.MONTHLY
              ? Math.ceil((subscriptionTotalDays ?? 0) / 30)
              : (subscriptionTotalDays ?? 0)

          // const expectedPriceCent = unitPriceCent * qty * count

          const res = await API.purchaseInstanceSubscription({
            domain: machine.domains?.[0] ?? 0,
            info,
            res_id: planItem.resID,
            qty,
            auto_renewal: autoRenewal,
            // order_to_billing: expirationBehavior,
            // check_price: expectedPriceCent > 0,
            // expected_price_cent: expectedPriceCent,
          })

          if (res.code === 0) {
            setIsConfirmModalOpen(false)
            navigate('/instances')
          }
        }
      } finally {
        setIsCreatingIns(false)
      }
    },
    [
      machine,
      count,
      checkedImage,
      checkedSnap,
      mountVolume,
      mountPath,
      readWriteMode,
      boundNAT,
      extraResourceDesc,
      billingMethod,
      // instancePlans,
      navigate,
      duration,
      subscriptionTotalDays,
      autoRenewal,
      expirationBehavior,
      // instancePlanByResID,
    ],
  )

  useEffect(() => {
    setMachine(undefined)
    setExtraResourceConfig(EMPTY_EXTRA_RESOURCE_CONFIG)
    autoExtraResourceConfigRef.current = EMPTY_EXTRA_RESOURCE_CONFIG
    isExtraResourceUserManagedRef.current = false
    resetMountConfig()
  }, [type, resetMountConfig])

  return (
    <div
      className="h-full relative"
      data-ai-component="create-instance-page"
      data-ai-state={
        canCreate ? 'ready-for-checkout' : machine ? 'needs-image-or-snapshot' : 'needs-machine'
      }
    >
      <div
        className="card !px-0 bg-panel-white h-full overflow-y-auto"
        data-ai-component="create-instance-workflow"
      >
        <div className="flex h-[60px] items-center gap-2 px-8">
          <div className="h6 mr-4">
            <Trans i18nKey="create.createNew">Create New</Trans>
          </div>
          {/* <InstanceImageSwitch value={viewMode} onChange={setViewMode} /> */}
        </div>

        <Divider className="px-8" />

        <div className="px-8">
          <div className="mb-4">
            <BillingMethods
              billingMethod={billingMethod}
              duration={duration}
              onBillingMethodChange={(value) => {
                const nextMethod = value as BillingMethod
                setBillingMethod(nextMethod)
                if (nextMethod === BILLING_METHODS.PAY_AS_YOU_GO) {
                  setSubscriptionTotalDays(undefined)
                  return
                }

                setBoundNAT(undefined)
                setBindNATModalOpen(false)
                setExtraResourceConfig(EMPTY_EXTRA_RESOURCE_CONFIG)
                autoExtraResourceConfigRef.current = EMPTY_EXTRA_RESOURCE_CONFIG
                isExtraResourceUserManagedRef.current = false
                const nextDuration = DURATION.DAILY
                setPurchaseFor(1)
                setDuration(nextDuration)
                setSubscriptionTotalDays(1)

                if (!supportsSubscription(machine, nextDuration)) {
                  setMachine(undefined)
                  setCheckedImage(undefined)
                  setCheckedSnap(undefined)
                  return
                }

                clearBootSourceIfIncompatible(count, false, 'subscription')
              }}
              onDurationChange={(value) => {
                const nextDuration = value as Duration
                setDuration(nextDuration)

                if (nextDuration === DURATION.DAILY) {
                  setPurchaseFor(1)
                  setSubscriptionTotalDays(1)
                } else {
                  setPurchaseFor(30)
                  setSubscriptionTotalDays(30)
                }

                if (billingMethod === BILLING_METHODS.SUBSCRIPTION) {
                  if (!supportsSubscription(machine, nextDuration)) {
                    setMachine(undefined)
                    setCheckedImage(undefined)
                    setCheckedSnap(undefined)
                  }
                }
              }}
            />
            {billingMethod === BILLING_METHODS.SUBSCRIPTION && (
              <div className="mt-4">
                <SubscriptionCreditAlert />
              </div>
            )}
          </div>
          <MachineSelector
            type={type}
            period={period}
            onTypeChange={setType}
            onChange={setMachine}
            count={count}
            checkedImage={checkedImage}
            setCheckedImage={setCheckedImage}
            checkedSnap={checkedSnap}
            setCheckedSnap={setCheckedSnap}
            enforceBootSourceResourceLimit
            supportsExtraResourcesForBootSource={billingMethod !== BILLING_METHODS.SUBSCRIPTION}
            selectedMachine={machine}
            billingMethod={billingMethod}
            duration={duration}
          />
        </div>

        <div className="mt-8 px-8">
          <div className="t-sb mb-2 text-secondary" id="instance-quantity-label">
            <Trans i18nKey="create.leasedQuantity">Unit Qty</Trans>
          </div>

          {/* Unit Quantity */}
          <SelectMachineTooltip enabled={!machine}>
            <NumberInput
              data-ai-component="quantity-control"
              data-ai-action="set-instance-quantity"
              data-ai-state={machine ? 'enabled' : 'disabled'}
              data-ai-value={count}
              aria-labelledby="instance-quantity-label"
              value={count}
              onChange={(value) => {
                setCount(value)
                clearBootSourceIfIncompatible(
                  value,
                  billingMethod !== BILLING_METHODS.SUBSCRIPTION,
                  'configuration',
                )
              }}
              min={Math.max(machine?.unitStep ?? 1, minimumBootSourceInstanceCount)}
              max={machine?.maxAvailUnits ?? 1}
              step={machine?.unitStep ?? 1}
              className="w-[260px]"
              disabled={!machine}
            />
          </SelectMachineTooltip>
        </div>

        {!isSubscription ? (
          <ExtraSpecificationSection
            machine={machine}
            value={extraResourceConfig}
            minimumExtraResourceConfig={requiredExtraResourceConfig}
            minimumResource={getMinResource(selectedBootSource)}
            minimumDiskGB={requiredExtraStorageGB}
            minimumRequirementSource={checkedSnap ? 'snapshot' : 'image'}
            onChange={handleExtraResourceChange}
          />
        ) : null}

        <MountDriveSection
          machine={machine}
          mountVolume={mountVolume}
          mountPath={mountPath}
          readWriteMode={readWriteMode}
          volumes={cloudVolumes}
          loading={volumesLoading}
          config={{
            ...PERSONAL_SECTION_CONFIG,
            getVolumeQuota: (vol) => (userStore.isOrderExpired ? vol.last_quota_gb : vol.quota_gb),
          }}
          setMountModalOpen={setMountModalOpen}
          resetMountConfig={resetMountConfig}
          navigate={navigate}
        />

        {billingMethod === BILLING_METHODS.PAY_AS_YOU_GO && (
          <BindPublicIPSection
            machine={machine}
            boundNAT={boundNAT}
            setBindNATModalOpen={setBindNATModalOpen}
            setBoundNAT={setBoundNAT}
          />
        )}

        {billingMethod === BILLING_METHODS.SUBSCRIPTION && (
          <>
            <PurchaseForSection
              duration={duration}
              purchaseFor={purchaseFor}
              subscriptionTotalDays={subscriptionTotalDays}
              onChange={({ purchaseFor, totalDays }) => {
                setPurchaseFor(purchaseFor)
                setSubscriptionTotalDays(totalDays)
              }}
            />
            <div className="px-8">
              <AutoRenewalSection value={autoRenewal} onChange={setAutoRenewal} className="w-44" />
              <div className="mt-6">
                <div className="t-sb text-secondary">
                  <div className="mb-2">
                    <Trans i18nKey="create.ExpirationBehaviorSection.expirationBehavior">
                      After Expiration
                    </Trans>
                  </div>
                  <div>
                    <ExpirationBehavior
                      value={expirationBehavior}
                      onChange={setExpirationBehavior}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        <div className="h-[92px]" aria-hidden="true" />

        <div className="absolute bottom-0 left-0 right-0 z-20 flex">
          <div className="w-full">
            <OrderSummary
              type={type}
              period={period}
              machine={machine}
              nat={billingMethod === BILLING_METHODS.PAY_AS_YOU_GO ? boundNAT : undefined}
              count={count}
              disabled={!canCreate || resourceRequirementStatus.insufficient}
              loading={priceLoading}
              priceAvailable={priceAvailable}
              extraStorageGB={extraStorageGB}
              extraCpuCores={extraResourceConfig.cpuCores}
              extraRamGB={extraResourceConfig.ramGB}
              requiredExtraStorageGB={minimumRequiredExtraStorageGB}
              availableExtraStorageGB={availableExtraStorageGB}
              storageInsufficient={storageInsufficient}
              priceMillicent={priceData?.discountPriceMillicent ?? priceData?.priceMillicent}
              basePriceMillicent={priceData?.basePriceMillicent}
              extraCpuPriceMillicent={extraCpuPriceMillicent}
              extraRamPriceMillicent={extraRamPriceMillicent}
              extraStoragePriceMillicent={extraStoragePriceMillicent}
              onConfirm={() => {
                if (storageInsufficient) {
                  setIsStorageWarningOpen(true)
                  return
                }
                if (
                  billingMethod === BILLING_METHODS.SUBSCRIPTION &&
                  subscriptionPriceCent !== undefined &&
                  rechargeCreditCent !== undefined &&
                  rechargeCreditCent < subscriptionPriceCent
                ) {
                  setInsufficientRechargeOpen(true)
                  return
                }
                setIsConfirmModalOpen(true)
              }}
              billingMethod={billingMethod}
              subscriptionTotalDays={
                billingMethod === BILLING_METHODS.SUBSCRIPTION ? subscriptionTotalDays : undefined
              }
              autoRenewal={billingMethod === BILLING_METHODS.SUBSCRIPTION ? autoRenewal : undefined}
              expirationBehavior={
                billingMethod === BILLING_METHODS.SUBSCRIPTION ? expirationBehavior : undefined
              }
              duration={duration}
            />
          </div>
        </div>
      </div>

      <ConfirmationModal
        open={isConfirmModalOpen}
        isCreatingIns={isCreatingIns}
        machine={machine}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={onConfirm}
      />

      <InsufficientStorageModal
        open={isStorageWarningOpen}
        requiredStorageGB={selectedMinDiskGB}
        baseStorageGB={baseStorageGB}
        maxExtraStorageGB={availableExtraStorageGB}
        onClose={() => setIsStorageWarningOpen(false)}
      />

      {insufficientRechargeOpen && (
        <InsufficientRechargeCreditsModal
          requiredCredits={`${formatPrice(subscriptionPriceCent ?? 0, false, '')}`}
          availableCredits={`${formatPrice(rechargeCreditCent ?? 0, false, '')}`}
          onClose={() => setInsufficientRechargeOpen(false)}
          onRecharge={() => {
            setInsufficientRechargeOpen(false)
            setExchargeModalOpen(true)
          }}
        />
      )}

      {exchargeModalOpen && (
        <ExchargeModal
          purpose="subscription"
          onOk={() => {
            setExchargeModalOpen(false)
            userStore.getAccount()
          }}
          onClose={() => setExchargeModalOpen(false)}
        />
      )}

      {mountModalOpen &&
        (cloudVolumes.length > 0 ? (
          <MountDriveModal
            open={mountModalOpen}
            mountVolume={mountVolume}
            volumes={cloudVolumes}
            mountPath={mountPath}
            readWriteMode={readWriteMode}
            machineDomains={machine?.domains}
            canWriteVolume={!userStore.isOrderExpired && !hasPrivateRegionDomain(machine?.domains)}
            getVolumeQuota={(vol) => (userStore.isOrderExpired ? vol.last_quota_gb : vol.quota_gb)}
            onChange={(volume, path, mode) => {
              updateMountConfig(volume, path, mode)
              setMountModalOpen(false)
            }}
            onClose={() => setMountModalOpen(false)}
          />
        ) : (
          <Modal open={mountModalOpen} onClose={() => setMountModalOpen(false)}>
            <Modal.Header>Datadrive</Modal.Header>
            <Modal.Body>
              <InsufficientQuotaView />
            </Modal.Body>
          </Modal>
        ))}

      {billingMethod === BILLING_METHODS.PAY_AS_YOU_GO && bindNATModalOpen && (
        <BindNATModal
          open={bindNATModalOpen}
          domain={machine?.domains?.[0]}
          boundNAT={boundNAT}
          onClose={() => setBindNATModalOpen(false)}
          onOk={(selectedIp) => {
            if (selectedIp) setBoundNAT(selectedIp)
            setBindNATModalOpen(false)
          }}
        />
      )}
    </div>
  )
}
